import { inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ComponentStore } from "@ngrx/component-store";
import { tapResponse } from "@ngrx/operators";
import { filter, mergeMap, pipe, switchMap, takeUntil, tap, withLatestFrom } from "rxjs";
import {
    ReportRow,
    ReportSort,
    ReportSortBy,
} from 'src/app/models/report.model';
import { SortDirection } from 'src/app/models/approval.model';
import { ApprovalApiService } from "src/app/services/approvals-api.service";
import { PageResult } from "src/app/shared/models/pageResult";
import { ManipulationsToolbarStore } from "src/app/store/manipulations-toolbar-store";
import { HttpErrorResponse } from "@angular/common/http";
import { Expense } from "src/app/models/Answer.model";
import { TostesStore } from "src/app/store/tostes-store";
import { TostPositionEnum, TostTypeEnum } from "src/app/shared/models/tostes.model";

export interface ReportState {
    reports: PageResult<ReportRow>;
    reportsExtra: PageResult<ReportRow>;
    readonly loading: boolean;
    readonly loadingExtra: boolean;
    isRequestOfExtaraWasEmpty: boolean;
    error?: string;
    // Collapsible reports screen: which rows are expanded, the expense rows loaded per row (lazy,
    // cached until the list reloads), and which rows are mid-fetch. Mirrors ApprovalStore's
    // expand/cache pattern, minus everything related to taking action (select/approve/reject).
    expandedIds: Record<number, boolean>;
    expenseRowsByApprovalId: Record<number, Expense[]>;
    loadingExpenseRowsIds: Record<number, boolean>;
    expenseRowsErrorByApprovalId: Record<number, string>;
}

export class ReportStore extends ComponentStore<ReportState> {
    #approvalApiService = inject(ApprovalApiService);
    #manipulationsToolbarStore = inject(ManipulationsToolbarStore);
    #tostesStore = inject(TostesStore);

    readonly reports$ = this.select((x) => x.reports)
    readonly reportsExtra$ = this.select((x) => x.reportsExtra)
    readonly loading$ = this.select((x) => x.loading);
    readonly loadingExtra$ = this.select((x) => x.loadingExtra);
    readonly isEmpty$ = this.select((x) => x.reports.isEmpty)
    readonly totals$ = this.select((x) => x.reports.totals)
    readonly expandedIds$ = this.select((x) => x.expandedIds)
    readonly expenseRowsByApprovalId$ = this.select((x) => x.expenseRowsByApprovalId)
    readonly loadingExpenseRowsIds$ = this.select((x) => x.loadingExpenseRowsIds)
    readonly expenseRowsErrorByApprovalId$ = this.select((x) => x.expenseRowsErrorByApprovalId)
    private currentSort: ReportSort = {
        sortBy: ReportSortBy.SentDate,
        sortDirection: SortDirection.Desc,
    };
    reportsExtraSig$ = toSignal(this.reportsExtra$)
    reportsSig$ = toSignal(this.reports$)
    loadingExtraSig$ = toSignal(this.loadingExtra$)

    constructor() {
        super({
            reports: { result: [], totals: 0 },
            reportsExtra: { result: [], totals: 0 },
            loading: false,
            loadingExtra: false,
            isRequestOfExtaraWasEmpty: false,
            expandedIds: {},
            expenseRowsByApprovalId: {},
            loadingExpenseRowsIds: {},
            expenseRowsErrorByApprovalId: {},
        });

        // Don't load reports before the real default filter is seeded - at construction time
        // selectedFilter$ replays the store's initial, filter-less state (ReportsComponent.ngOnInit
        // sets the real 60-day createDate range right after), and loading against that placeholder
        // would just be a redundant getReports/getExtraReports round trip immediately superseded.
        // createDate is the equivalent here of ApprovalStore's approvalsTabs guard.
        this.#manipulationsToolbarStore.selectedFilter$
            .pipe(
                filter((selectedFilter) => selectedFilter?.createDate !== undefined),
                takeUntil(this.destroy$))
            .subscribe(() => {
                this.getReports();
            });

    }
    changeSort(sort: ReportSort): void {
        this.currentSort = sort;

        this.patchState({
            reports: {
                result: [],
                totals: 0,
            },
            reportsExtra: {
                result: [],
                totals: 0,
            },
            isRequestOfExtaraWasEmpty: false,
        });

        this.getReports(true);
    }
    readonly getReports = this.effect<void | boolean>(
        pipe(
            tap(() => this.patchState({ loading: true })),
            tap((ev) => {
                if (ev) {
                    this.#manipulationsToolbarStore.resetPage();
                }
            }),
            withLatestFrom(this.#manipulationsToolbarStore.selectedFilter$, this.#manipulationsToolbarStore.page$),
            switchMap(([event, selectedFilter, page]) => this.#approvalApiService.getReports(event ? 1 : page, selectedFilter, this.currentSort)),
            tap((reports: PageResult<ReportRow>) => this.patchState({ reports })),
            tap(() => {
                this.patchState({ loading: false })
                this.loadReportsExtra();
            }),
        )
    );

    readonly loadReportsExtra = this.effect<void | boolean>(
        pipe(
            tap(() => this.patchState({ loadingExtra: true })),
            tap(() => this.#manipulationsToolbarStore.updatePage()),
            withLatestFrom(this.#manipulationsToolbarStore.selectedFilter$, this.#manipulationsToolbarStore.page$),
            switchMap(([_, selectedFilter, page]) => this.#approvalApiService.getReportsExtra(page, selectedFilter, this.currentSort)),
            tap((reportsExtra: PageResult<ReportRow>) => this.patchState({ reportsExtra })),
            tap(() => {
                this.patchState({ loadingExtra: false })
            })
        )
    );

    readonly updateReportsWithExtra = this.updater((state) => (
        {
            ...state,
            isRequestOfExtaraWasEmpty: ((this.reportsExtraSig$()?.result == undefined || this.reportsExtraSig$()?.result.length == 0) && this.loadingExtraSig$()) ? true : false,
            reports: {
                result: [...(this.reportsSig$()?.result || []), ...(this.reportsExtraSig$()?.result || [])],
                totals: this.reportsSig$()?.totals || 0
            },
            reportsExtra: { result: [], totals: 0 }
        }
    ));

    // Expand/collapse a row's header; the first time it opens, lazy-load and cache its expense
    // rows (collapsing again keeps the cache, so re-opening doesn't refetch).
    readonly toggleExpand = this.effect<ReportRow>((report$) =>
        report$.pipe(
            withLatestFrom(this.state$),
            tap(([report, state]) => {
                const isExpanding = !state.expandedIds[report.id];
                this.patchState({ expandedIds: { ...state.expandedIds, [report.id]: isExpanding } });
                if (isExpanding && !state.expenseRowsByApprovalId[report.id]) {
                    if (report.recipientGuid) {
                        this.loadExpenseRows({ approvalId: report.id, guid: report.recipientGuid });
                    } else {
                        this.patchState((s) => ({
                            expenseRowsErrorByApprovalId: { ...s.expenseRowsErrorByApprovalId, [report.id]: 'Missing recipient guid' }
                        }));
                    }
                }
            })
        )
    );

    // Global expand/collapse-all: additive to the existing per-row toggleExpand, doesn't replace it.
    // If every currently-loaded row is already expanded, collapses them all; otherwise expands them
    // all, lazy-loading expense rows for whichever ones aren't cached yet.
    readonly toggleExpandAll = this.effect<ReportRow[]>((reports$) =>
        reports$.pipe(
            withLatestFrom(this.state$),
            tap(([reports, state]) => {
                const allExpanded = reports.length > 0 && reports.every((r) => !!state.expandedIds[r.id]);
                this.patchState({
                    expandedIds: allExpanded ? {} : Object.fromEntries(reports.map((r) => [r.id, true]))
                });
                if (!allExpanded) {
                    reports.forEach((r) => {
                        if (state.expenseRowsByApprovalId[r.id]) return;
                        if (r.recipientGuid) {
                            this.loadExpenseRows({ approvalId: r.id, guid: r.recipientGuid });
                        } else {
                            this.patchState((s) => ({
                                expenseRowsErrorByApprovalId: { ...s.expenseRowsErrorByApprovalId, [r.id]: 'Missing recipient guid' }
                            }));
                        }
                    });
                }
            })
        )
    );

    // mergeMap, not switchMap: fires once per row being expanded, so switchMap wouldn't let
    // several near-simultaneous expansions all load their own content in parallel.
    readonly loadExpenseRows = this.effect<{ approvalId: number; guid: string }>(
        pipe(
            tap(({ approvalId }) =>
                this.patchState((state) => ({
                    loadingExpenseRowsIds: { ...state.loadingExpenseRowsIds, [approvalId]: true },
                    expenseRowsErrorByApprovalId: { ...state.expenseRowsErrorByApprovalId, [approvalId]: '' }
                }))
            ),
            mergeMap(({ approvalId, guid }) =>
                // Reused as-is: getExpenseRows is already a generic endpoint, not approvals-specific.
                this.#approvalApiService.getExpenseRows(guid).pipe(
                    tapResponse(
                        (rows) =>
                            this.patchState((state) => ({
                                expenseRowsByApprovalId: { ...state.expenseRowsByApprovalId, [approvalId]: rows },
                                loadingExpenseRowsIds: { ...state.loadingExpenseRowsIds, [approvalId]: false }
                            })),
                        (error: HttpErrorResponse) => {
                            const message = error.error?.message || error.error || error.message || 'Unknown error';
                            this.patchState((state) => ({
                                loadingExpenseRowsIds: { ...state.loadingExpenseRowsIds, [approvalId]: false },
                                expenseRowsErrorByApprovalId: { ...state.expenseRowsErrorByApprovalId, [approvalId]: message }
                            }));
                            this.#tostesStore.addTost({
                                guid: this.#tostesStore.generateGuid(),
                                message: { en: "An error occurred while loading the expense rows", he: "ארעה שגיאה בטעינת שורות ההוצאה" },
                                position: TostPositionEnum.BottomLeft, seconds: 5, type: TostTypeEnum.ERROR
                            });
                        }
                    )
                )
            )
        )
    );
}
